import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Ban } from 'lucide-react';
import { delegationsApi } from '../../../api/admin/delegations';
import { genericDocumentApi } from '../../../api/generic-documents';
import { ApiError } from '../../../api/client';
import { PageHeader } from '../../../components/PageHeader';
import { StatusBadge } from '../../../components/StatusBadge';
import { CommentsThread } from '../../../components/CommentsThread';
import { DocumentsSection } from '../../../components/DocumentsSection';
import { WorkflowTrackingTab } from '../../../components/WorkflowTrackingTab';
import { DELEGATION_STAGES } from '../../../config/workflow-stages';
import { WorkspaceTabs } from '../../../components/WorkspaceTabs';
import { useToast } from '../../../components/Toast';
import { usePermission } from '../../../auth/AuthContext';
import { formatDate } from '../../../utils/formatDate';
import { useUserName } from '../../../hooks/useUserName';

export function DelegationDetailPage() {
  const { id } = useParams<{ id: string }>();
  const delegationId = Number(id);
  const { showSuccess, showError } = useToast();
  const queryClient = useQueryClient();
  const canManage = usePermission('admin.delegations.manage');

  const listQuery = useQuery({ queryKey: ['delegations'], queryFn: () => delegationsApi.list() });
  const { userName } = useUserName();
  const docsQuery = useQuery({ queryKey: ['delegation-documents', delegationId], queryFn: () => genericDocumentApi.listFor('delegation', delegationId) });

  const cancelMutation = useMutation({
    mutationFn: () => delegationsApi.cancel(delegationId),
    onSuccess: () => { showSuccess('Delegation cancelled.'); queryClient.invalidateQueries({ queryKey: ['delegations'] }); },
    onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed to cancel delegation.'),
  });


  if (!listQuery.data) return <div className="page"><p className="muted">Loading…</p></div>;
  const d = listQuery.data.data.find((del) => del.id === delegationId);
  if (!d) return <div className="page"><p className="muted">Delegation not found.</p></div>;

  return (
    <div className="page">
      <Link to="/admin/delegations">&larr; Back to Delegations</Link>
      <PageHeader
        title={`${userName(d.delegator_user_id)} \u2192 ${userName(d.delegate_user_id)}`}
        actions={canManage && d.status !== 'cancelled' && <button className="button-danger" onClick={() => cancelMutation.mutate()}><Ban size={16} className="inline-icon" /> Cancel</button>}
        readOnly={!canManage}
      />
      <p className="muted">{formatDate(d.start_date)} → {formatDate(d.end_date)} · <StatusBadge status={d.status} /></p>

      <WorkspaceTabs
        tabs={[
          {
            key: 'details',
            label: 'Details',
            content: (
              <>
                {d.reason && (
                  <section className="card">
                    <h3>Reason</h3>
                    <p>{d.reason}</p>
                  </section>
                )}
                <CommentsThread entityType="delegation" entityId={delegationId} />
              </>
            ),
          },
          {
            key: 'documents',
            label: 'Documents',
            content: (
              <DocumentsSection
                documents={docsQuery.data?.data ?? []}
                downloadUrl={(documentId) => genericDocumentApi.downloadUrl('delegation', documentId)}
                upload={(input) => genericDocumentApi.uploadFor('delegation', delegationId, input)}
                onDelete={(documentId) => genericDocumentApi.deleteFor('delegation', documentId)}
                queryKeyToInvalidate={['delegation-documents', delegationId]}
                hrCategories={false}
              />
            ),
          },
          {
            key: 'tracking',
            label: 'Tracking',
            content: <WorkflowTrackingTab entityType="delegation" entityId={delegationId} actionStageMap={DELEGATION_STAGES} />,
          },
        ]}
      />
    </div>
  );
}
